/* Maison Soleil — theme behaviours: cart drawer (AJAX), product form,
   variant picker, quantity steppers, mobile gallery dots. Vanilla JS, no deps. */
(function () {
  'use strict';

  const root = document.documentElement;
  const config = window.themeConfig || {};
  const routeRoot = config.rootUrl || '/';
  const money = (cents) =>
    (window.Shopify && Shopify.formatMoney
      ? Shopify.formatMoney(cents)
      : new Intl.NumberFormat(config.locale || document.documentElement.lang || 'it', {
          style: 'currency', currency: config.currency || 'EUR'
        }).format(cents / 100));

  /* ----------------------------------------------------------------- Cart */
  const Cart = {
    drawer: () => document.querySelector('[data-cart-drawer]'),

    open() {
      const d = Cart.drawer();
      if (!d) return;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      root.classList.add('cart-open');
    },

    close() {
      const d = Cart.drawer();
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      root.classList.remove('cart-open');
    },

    async add(form) {
      const btn = form.querySelector('[type="submit"]');
      const formData = new FormData(form);
      formData.set('sections', 'cart-drawer');
      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
      try {
        const res = await fetch(routeRoot + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) throw data;
        await Cart.refresh(data.sections && data.sections['cart-drawer']);
        Cart.open();
      } catch (err) {
        const msg = (err && err.description) || config.cartError || 'Unable to add this item.';
        const target = form.querySelector('[data-cart-error]');
        if (target) target.textContent = msg;
        else console.warn('Cart add error:', err);
      } finally {
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
      }
    },

    async addById(id, button) {
      if (button) { button.classList.add('is-loading'); button.disabled = true; }
      try {
        const res = await fetch(routeRoot + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1, sections: 'cart-drawer' }),
        });
        const data = await res.json();
        if (!res.ok) throw data;
        await Cart.refresh(data.sections && data.sections['cart-drawer']);
        if (window.MaisonQuickAdd) window.MaisonQuickAdd.close();
        Cart.open();
      } catch (err) {
        const msg = (err && err.description) || config.cartError || 'Unable to add this item.';
        const errorEl = document.querySelector('[data-quickadd-body] [data-cart-error]');
        if (errorEl) errorEl.textContent = msg;
      } finally {
        if (button) { button.classList.remove('is-loading'); button.disabled = false; }
      }
    },

    async change(line, quantity) {
      const res = await fetch(routeRoot + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: 'cart-drawer' }),
      });
      const data = await res.json();
      Cart.refresh(data.sections && data.sections['cart-drawer']);
    },

    /* Replace drawer inner markup with freshly-rendered section HTML. */
    async refresh(sectionHTML) {
      let html = sectionHTML;
      if (!html) {
        const res = await fetch(routeRoot + '?section_id=cart-drawer');
        html = await res.text();
      }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-inner]');
      const current = document.querySelector('[data-cart-inner]');
      if (fresh && current) current.replaceWith(fresh);
      Cart.updateCount(parsed);
    },

    updateCount(parsed) {
      const src = parsed || document;
      const countEl = src.querySelector('[data-cart-count-value]');
      const count = countEl ? parseInt(countEl.textContent, 10) || 0 : null;
      if (count === null) return;
      document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },
  };
  window.MaisonCart = Cart;

  /* --------------------------------------------------------- Search drawer */
  const Search = {
    el: () => document.querySelector('[data-search-drawer]'),
    input: () => document.querySelector('[data-predictive-input]'),
    results: () => document.querySelector('[data-predictive-results]'),
    _t: null, // typewriter timer
    _debounce: null,

    open() {
      const d = Search.el();
      if (!d) return;
      document.querySelectorAll('details[scroll-lock][open]').forEach((x) => x.removeAttribute('open'));
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      root.classList.add('cart-open');
      setTimeout(() => { const i = Search.input(); if (i) i.focus(); }, 50);
      Search.startTypewriter();
    },
    close() {
      const d = Search.el();
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      root.classList.remove('cart-open');
      Search.stopTypewriter();
    },
    clear() {
      const i = Search.input();
      if (i) { i.value = ''; i.focus(); }
      const r = Search.results();
      if (r) r.innerHTML = '';
      const c = document.querySelector('[data-search-clear]');
      if (c) c.hidden = true;
      Search.startTypewriter();
    },

    async query(term) {
      const r = Search.results();
      if (!r) return;
      if (!term || term.length < 2) { r.innerHTML = ''; return; }
      try {
        const url = `${routeRoot}search/suggest?q=${encodeURIComponent(term)}&section_id=search-drawer&resources[type]=product,collection&resources[limit]=8`;
        const res = await fetch(url);
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('[data-predictive-results]');
        if (fresh) r.innerHTML = fresh.innerHTML;
        Search.applyTab();
      } catch (e) { /* ignore */ }
    },

    applyTab() {
      const active = document.querySelector('[data-search-tabs] .is-active');
      const tab = active ? active.getAttribute('data-tab') : 'products';
      document.querySelectorAll('.search-results__group').forEach((g) => {
        g.hidden = g.getAttribute('data-group') !== tab;
      });
    },

    /* Typewriter placeholder cycling through example queries */
    startTypewriter() {
      const i = Search.input();
      if (!i || !i.dataset.typewriter) return;
      if (i.value) return;
      let phrases;
      try { phrases = JSON.parse(i.dataset.typewriter); } catch (e) { return; }
      if (!phrases || !phrases.length) return;
      Search.stopTypewriter();
      let p = 0, c = 0, deleting = false;
      const tick = () => {
        if (i.value) { Search.stopTypewriter(); return; }
        const word = phrases[p];
        c += deleting ? -1 : 1;
        i.setAttribute('placeholder', word.slice(0, c) + '​');
        let delay = deleting ? 40 : 90;
        if (!deleting && c === word.length) { deleting = true; delay = 1400; }
        else if (deleting && c === 0) { deleting = false; p = (p + 1) % phrases.length; delay = 300; }
        Search._t = setTimeout(tick, delay);
      };
      tick();
    },
    stopTypewriter() { if (Search._t) { clearTimeout(Search._t); Search._t = null; } },
  };
  window.MaisonSearch = Search;

  /* ----------------------------------------------------- Quick-add sheet */
  const QuickAdd = {
    el: () => document.querySelector('[data-quickadd-drawer]'),
    body: () => document.querySelector('[data-quickadd-body]'),
    open() {
      const d = QuickAdd.el();
      if (!d) return;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      root.classList.add('cart-open');
    },
    close() {
      const d = QuickAdd.el();
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      root.classList.remove('cart-open');
    },
    async load(url) {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      try {
        const res = await fetch(url + sep + 'section_id=quick-add');
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('.quickadd__content');
        const body = QuickAdd.body();
        if (content && body) {
          body.innerHTML = content.outerHTML;
          QuickAdd.initSheet();
          QuickAdd.open();
        } else {
          window.location.href = url;
        }
      } catch (e) {
        window.location.href = url;
      }
    },
    // Two-option sheet (colour + size): resolve the variant from colour + size.
    initSheet() {
      const body = QuickAdd.body();
      if (!body) return;
      const json = body.querySelector('[data-qa-variants]');
      const colorsWrap = body.querySelector('[data-qa-colors]');
      const sizesWrap = body.querySelector('[data-qa-sizes]');
      if (!json || !colorsWrap || !sizesWrap) return; // single-option sheet
      let variants;
      try { variants = JSON.parse(json.textContent); } catch (e) { return; }

      const selectedColor = () => {
        const c = colorsWrap.querySelector('.is-selected');
        return c ? c.dataset.qaColor : null;
      };

      function refreshSizes() {
        const color = selectedColor();
        sizesWrap.querySelectorAll('[data-qa-size]').forEach((btn) => {
          const v = variants.find((x) => x.color === color && x.size === btn.dataset.qaSize);
          const ok = v && v.available;
          btn.disabled = !ok;
          btn.classList.toggle('is-out', !ok);
          if (v) btn.setAttribute('data-qa-variant', v.id);
          else btn.removeAttribute('data-qa-variant');
        });
      }

      colorsWrap.addEventListener('click', (e) => {
        const c = e.target.closest('[data-qa-color]');
        if (!c) return;
        e.preventDefault();
        colorsWrap.querySelectorAll('[data-qa-color]').forEach((b) => b.classList.toggle('is-selected', b === c));
        refreshSizes();
      });

      refreshSizes();
    },
  };
  window.MaisonQuickAdd = QuickAdd;

  /* -------------------------------------------------------- Filters drawer */
  const Filters = {
    el: () => document.querySelector('[data-filters-drawer]'),
    open() {
      const d = Filters.el();
      if (!d) return;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      root.classList.add('cart-open');
    },
    close() {
      const d = Filters.el();
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      root.classList.remove('cart-open');
    },
  };

  /* ------------------------------------------- Collection view switcher */
  function initCollectionView() {
    const grid = document.querySelector('[data-coll-grid]');
    const views = document.querySelector('[data-coll-views]');
    if (!grid || !views) return;
    const apply = (v) => {
      grid.classList.remove('is-view-1', 'is-view-2', 'is-view-4');
      grid.classList.add('is-view-' + v);
      views.querySelectorAll('[data-view]').forEach((b) =>
        b.classList.toggle('is-active', b.getAttribute('data-view') === v)
      );
    };
    let saved = '2';
    try { saved = localStorage.getItem('coll_view') || '2'; } catch (e) {}
    apply(saved);
    views.addEventListener('click', (e) => {
      const b = e.target.closest('[data-view]');
      if (!b) return;
      const v = b.getAttribute('data-view');
      try { localStorage.setItem('coll_view', v); } catch (e2) {}
      apply(v);
    });
  }

  /* ------------------------------------------------------- Event wiring */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    if (e.submitter && e.submitter.name !== 'add') return;
    e.preventDefault();
    Cart.add(form);
  });

  document.addEventListener('click', (e) => {
    // Custom sort dropdown (collection)
    const sortForm = e.target.closest('[data-sort-form]');
    // close any open sort menu not belonging to the clicked form
    document.querySelectorAll('[data-sort-form]').forEach((f) => {
      if (f !== sortForm) {
        const m = f.querySelector('[data-sort-menu]');
        const t = f.querySelector('[data-sort-toggle]');
        if (m) m.setAttribute('hidden', '');
        if (t) t.setAttribute('aria-expanded', 'false');
      }
    });
    const sortToggle = e.target.closest('[data-sort-toggle]');
    if (sortToggle && sortForm) {
      e.preventDefault();
      const menu = sortForm.querySelector('[data-sort-menu]');
      const isOpen = !menu.hasAttribute('hidden');
      menu.toggleAttribute('hidden', isOpen);
      sortToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      return;
    }
    const sortOpt = e.target.closest('[data-sort-value]');
    if (sortOpt && sortForm) {
      e.preventDefault();
      sortForm.querySelector('[data-sort-input]').value = sortOpt.getAttribute('data-sort-value');
      if (sortForm.requestSubmit) sortForm.requestSubmit();
      else sortForm.submit();
      return;
    }

    // Open drawer from header / triggers
    const opener = e.target.closest('[data-cart-open]');
    if (opener) { e.preventDefault(); Cart.open(); return; }

    // Close cart drawer. If the target is a real link, let it navigate.
    const cartClose = e.target.closest('[data-cart-close]');
    if (cartClose) {
      const isLink = cartClose.tagName === 'A' && cartClose.getAttribute('href');
      if (!isLink) e.preventDefault();
      Cart.close();
      return;
    }

    // Close header menu drawer (<details scroll-lock>)
    const drawerClose = e.target.closest('[data-drawer-close]');
    if (drawerClose) {
      e.preventDefault();
      const d = drawerClose.closest('details[scroll-lock]');
      if (d) d.removeAttribute('open');
      return;
    }

    // Quick-add: "+" on a product card
    const qa = e.target.closest('[data-quick-add]');
    if (qa) {
      e.preventDefault();
      e.stopPropagation();
      const vid = qa.getAttribute('data-variant-id');
      if (vid) Cart.addById(vid, qa);
      else QuickAdd.load(qa.getAttribute('data-url'));
      return;
    }
    // Quick-add: size chosen in the sheet
    const qaVar = e.target.closest('[data-qa-variant]');
    if (qaVar) {
      e.preventDefault();
      Cart.addById(qaVar.getAttribute('data-qa-variant'), qaVar);
      return;
    }
    if (e.target.closest('[data-quickadd-close]')) { e.preventDefault(); QuickAdd.close(); return; }

    // Checkout buttons: show loading while the page navigates (no preventDefault)
    const checkoutBtn = e.target.closest('button[name="checkout"]');
    if (checkoutBtn && !checkoutBtn.disabled) { checkoutBtn.classList.add('is-loading'); }

    // Search drawer
    if (e.target.closest('[data-search-open]')) { e.preventDefault(); Search.open(); return; }
    const searchClose = e.target.closest('[data-search-close]');
    if (searchClose) {
      const isLink = searchClose.tagName === 'A' && searchClose.getAttribute('href');
      if (!isLink) e.preventDefault();
      Search.close();
      return;
    }
    if (e.target.closest('[data-search-clear]')) { e.preventDefault(); Search.clear(); return; }
    const tab = e.target.closest('[data-search-tabs] .search-drawer__tab');
    if (tab) {
      e.preventDefault();
      document.querySelectorAll('[data-search-tabs] .search-drawer__tab').forEach((t) => t.classList.toggle('is-active', t === tab));
      Search.applyTab();
      return;
    }

    // Filters drawer
    if (e.target.closest('[data-filters-open]')) { e.preventDefault(); Filters.open(); return; }
    if (e.target.closest('[data-filters-close]')) { e.preventDefault(); Filters.close(); return; }

    // Carousel arrows (category banners)
    const cbBtn = e.target.closest('[data-cb-scroll]');
    if (cbBtn) {
      e.preventDefault();
      const wrap = cbBtn.closest('.category-banners__carousel');
      const track = wrap && wrap.querySelector('[data-cb-track]');
      if (track) {
        const dir = parseInt(cbBtn.getAttribute('data-cb-scroll'), 10) || 1;
        track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: 'smooth' });
      }
      return;
    }

    // PDP quantity stepper (+/-)
    const qtyStep = e.target.closest('[data-qty-step]');
    if (qtyStep) {
      e.preventDefault();
      const wrap = qtyStep.closest('[data-qty-wrap]');
      const input = wrap && wrap.querySelector('[data-product-quantity]');
      if (input) {
        const step = parseInt(input.step, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = input.max ? parseInt(input.max, 10) : Infinity;
        let val = (parseInt(input.value, 10) || min) + parseInt(qtyStep.getAttribute('data-qty-step'), 10) * step;
        val = Math.max(min, Math.min(max, val));
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const minus = wrap.querySelector('[data-qty-step="-1"]');
        const plus = wrap.querySelector('[data-qty-step="1"]');
        if (minus) minus.disabled = val <= min;
        if (plus) plus.disabled = max !== Infinity && val >= max;
      }
      return;
    }

    // Remove / change line item
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const line = parseInt(qtyBtn.getAttribute('data-line'), 10);
      const qty = parseInt(qtyBtn.getAttribute('data-qty-change'), 10);
      Cart.change(line, qty);
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      Cart.change(parseInt(removeBtn.getAttribute('data-line'), 10), 0);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    Cart.close();
    Search.close();
    Filters.close();
    QuickAdd.close();
    document.querySelectorAll('details[scroll-lock][open]').forEach((d) => d.removeAttribute('open'));
    document.querySelectorAll('[data-sort-menu]:not([hidden])').forEach((m) => {
      m.setAttribute('hidden', '');
      const t = m.closest('[data-sort-form]')?.querySelector('[data-sort-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  });

  // Predictive search input (debounced)
  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-predictive-input]');
    if (!input) return;
    const clear = document.querySelector('[data-search-clear]');
    if (clear) clear.hidden = !input.value;
    if (input.value) Search.stopTypewriter();
    clearTimeout(Search._debounce);
    Search._debounce = setTimeout(() => Search.query(input.value.trim()), 220);
  });

  // Header: transparent → solid on scroll
  const headerEl = document.querySelector('[data-header]');
  if (headerEl && headerEl.classList.contains('header--overlay')) {
    const onScroll = () => headerEl.classList.toggle('is-scrolled', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ----------------------------------------------- Variant picker (PDP) */
  function initVariantPickers(scope) {
    (scope || document).querySelectorAll('[data-variant-picker]').forEach((picker) => {
      if (picker.dataset.bound) return;
      picker.dataset.bound = '1';
      const variants = JSON.parse(picker.querySelector('[data-variant-json]').textContent);
      const form = document.querySelector(picker.getAttribute('data-form'));
      const component = picker.closest('.shopify-section') || picker.closest('.gift-feature') || document;
      const idInput = form && form.querySelector('input[name="id"]');
      const priceEl = component.querySelector('[data-product-price]');
      const compareEl = component.querySelector('[data-product-compare]');
      const unitPriceEl = component.querySelector('[data-product-unit-price]');
      const availEl = component.querySelector('[data-product-availability]');
      const scarcityEl = component.querySelector('[data-scarcity-badge]');
      const addBtn = form && form.querySelector('[type="submit"]');
      const stickyPriceEl = component.querySelector('[data-sticky-price]');
      const stickyBtn = component.querySelector('[data-sticky-add]');
      const quantityInput = component.querySelector('[data-product-quantity]');

      function selectedOptions() {
        const opts = [];
        picker.querySelectorAll('[data-option-index]').forEach((group) => {
          const idx = parseInt(group.getAttribute('data-option-index'), 10);
          const checked = group.querySelector('input:checked');
          if (checked) opts[idx] = checked.value;
        });
        return opts;
      }

      function matchVariant(opts) {
        return variants.find((v) =>
          opts.every((o, i) => v.options[i] === o)
        );
      }

      function update() {
        const opts = selectedOptions();
        const variant = matchVariant(opts);
        if (!variant) return;
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.innerHTML = money(variant.price);
        if (stickyPriceEl) stickyPriceEl.innerHTML = money(variant.price);
        // keep pay-later provider widgets in sync with the selected variant
        const scalapayEl = component.querySelector('[data-paylater-scalapay]');
        if (scalapayEl) scalapayEl.setAttribute('amount', (variant.price / 100).toFixed(2));
        const klarnaEl = component.querySelector('[data-paylater-klarna]');
        if (klarnaEl) {
          klarnaEl.setAttribute('data-purchase-amount', variant.price);
          if (window.KlarnaOnsiteService) window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
        }
        if (stickyBtn) {
          stickyBtn.disabled = !variant.available;
          const sl = stickyBtn.querySelector('[data-sticky-label]') || stickyBtn;
          sl.textContent = variant.available
            ? stickyBtn.getAttribute('data-label-add')
            : stickyBtn.getAttribute('data-label-soldout');
        }
        if (compareEl) {
          if (variant.compare_at_price > variant.price) {
            compareEl.innerHTML = money(variant.compare_at_price);
            compareEl.hidden = false;
          } else compareEl.hidden = true;
        }
        if (unitPriceEl) {
          unitPriceEl.textContent = variant.unit_price_text || '';
          unitPriceEl.hidden = !variant.unit_price_text;
        }
        if (quantityInput) {
          quantityInput.min = variant.quantity_min || 1;
          quantityInput.step = variant.quantity_increment || 1;
          if (variant.quantity_max) quantityInput.max = variant.quantity_max;
          else quantityInput.removeAttribute('max');
          if (parseInt(quantityInput.value, 10) < variant.quantity_min) quantityInput.value = variant.quantity_min;
        }
        const pickup = component.querySelector('[data-pickup-availability]');
        const productSection = component.querySelector('[data-product-section]');
        if (pickup && productSection && productSection.dataset.productUrl) {
          const pickupUrl = `${productSection.dataset.productUrl}?variant=${variant.id}&section_id=${pickup.dataset.sectionId}`;
          fetch(pickupUrl)
            .then((response) => response.text())
            .then((html) => {
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const fresh = parsed.querySelector('[data-pickup-availability]');
              if (fresh) pickup.innerHTML = fresh.innerHTML;
            })
            .catch(() => {});
        }
        if (availEl) {
          availEl.dataset.available = variant.available ? 'true' : 'false';
        }
        if (scarcityEl) {
          scarcityEl.hidden = !variant.available;
        }
        if (addBtn) {
          addBtn.disabled = !variant.available;
          const label = addBtn.querySelector('[data-add-label]') || addBtn;
          label.textContent = variant.available
            ? addBtn.getAttribute('data-label-add')
            : addBtn.getAttribute('data-label-soldout');
        }
        // reflect availability on swatch/size labels
        picker.querySelectorAll('[data-option-index]').forEach((group) => {
          const idx = parseInt(group.getAttribute('data-option-index'), 10);
          group.querySelectorAll('input').forEach((input) => {
            const test = opts.slice();
            test[idx] = input.value;
            const partial = variants.filter((v) =>
              test.every((o, i) => i === idx ? v.options[i] === input.value : (o == null || v.options[i] === o))
            );
            const some = partial.some((v) => v.available);
            input.closest('label').classList.toggle('is-unavailable', !some);
          });
        });
        if (window.location.pathname.startsWith('/products/')) {
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
        return variant;
      }

      // Keep ALL photos visible; on colour change scroll the matching photo to front.
      function focusGalleryColor() {
        const colorInput = picker.querySelector('.pdp__option--color input:checked');
        const track = component.querySelector('[data-gallery-track]');
        if (!track || !colorInput) return;
        const items = track.querySelectorAll('.pdp__gallery-item');
        const dots = component.querySelectorAll('[data-gallery-dots] [data-gallery-dot]');
        const color = colorInput.value.toLowerCase();
        let target = -1;
        items.forEach((it, i) => {
          if (target === -1 && it.dataset.mediaColor === color) target = i;
        });
        if (target === -1) return; // no photo set for this colour: leave gallery as is
        dots.forEach((d, i) => d.classList.toggle('is-active', i === target));
        if (window.matchMedia('(max-width: 749px)').matches) {
          track.scrollTo({ left: items[target].offsetLeft - track.offsetLeft, behavior: 'smooth' });
        } else {
          items[target].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      picker.addEventListener('change', () => {
        const variant = update();
        const media = variant && variant.featured_media_id
          ? component.querySelector(`[data-media-id="${variant.featured_media_id}"]`)
          : null;
        if (media) {
          const track = component.querySelector('[data-gallery-track]');
          if (window.matchMedia('(max-width: 749px)').matches && track) track.scrollTo({ left: media.offsetLeft - track.offsetLeft, behavior: 'smooth' });
          else media.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else focusGalleryColor();
      });
      update();
    });
  }

  /* --------------------------------------------- Mobile gallery dots */
  function initGalleries(scope) {
    (scope || document).querySelectorAll('[data-gallery-track]').forEach((track) => {
      if (track.dataset.bound) return;
      track.dataset.bound = '1';
      const dots = track.parentElement.querySelectorAll('[data-gallery-dot]');
      const items = track.querySelectorAll('.pdp__gallery-item');
      if (!dots.length) return;
      track.addEventListener('scroll', () => {
        // index among currently visible items (hidden ones have no width)
        const visIndex = Math.round(track.scrollLeft / track.clientWidth);
        const visible = [...items].map((it, n) => n).filter((n) => !items[n].hidden);
        const active = visible[visIndex];
        dots.forEach((d, n) => d.classList.toggle('is-active', n === active));
      }, { passive: true });
    });
  }

  /* --------------------------------------------- Sticky add bar (PDP) */
  function initStickyBar(scope) {
    (scope || document).querySelectorAll('[data-pdp-sticky]').forEach((bar) => {
      if (bar.dataset.bound) return;
      bar.dataset.bound = '1';
      const anchor = document.querySelector('[data-product-form] [type="submit"]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      bar.hidden = false;
      const io = new IntersectionObserver((entries) => {
        // show the bar once the main button has scrolled out of view
        bar.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { rootMargin: '0px 0px -10% 0px' });
      io.observe(anchor);
    });
  }

  /* ------------------------------------------- Product recommendations */
  function initRecommendations(scope) {
    (scope || document).querySelectorAll('[data-recommendations]').forEach((el) => {
      const url = el.getAttribute('data-url');
      if (!url || el.dataset.loaded) return;
      el.dataset.loaded = '1';
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('[data-recommendations]');
          if (fresh && fresh.querySelector('.recommend__item')) {
            fresh.dataset.loaded = '1';
            el.replaceWith(fresh);
          }
        })
        .catch(() => {});
    });
  }

  /* ----------------------------------------------- Recently viewed products */
  function initRecentlyViewed(scope) {
    (scope || document).querySelectorAll('[data-recently-viewed]').forEach(async (section) => {
      if (section.dataset.bound) return;
      section.dataset.bound = '1';
      const currentHandle = section.dataset.currentProduct;
      const limit = parseInt(section.dataset.limit, 10) || 6;
      const track = section.querySelector('[data-recently-viewed-track]');
      if (!currentHandle || !track) return;

      const storageKey = 'maison_recent_products_v1';
      let history = [];
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
        if (Array.isArray(stored)) history = stored.filter((handle) => typeof handle === 'string');
      } catch (error) { history = []; }

      const previous = history.filter((handle) => handle !== currentHandle).slice(0, limit);
      const nextHistory = [currentHandle, ...history.filter((handle) => handle !== currentHandle)].slice(0, 20);
      try { localStorage.setItem(storageKey, JSON.stringify(nextHistory)); } catch (error) {}
      if (!previous.length) return;

      const cards = await Promise.all(previous.map(async (handle) => {
        try {
          const response = await fetch(`${routeRoot}products/${encodeURIComponent(handle)}?section_id=recently-viewed-item`);
          if (!response.ok) return null;
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          return parsed.querySelector('[data-recently-viewed-item]');
        } catch (error) { return null; }
      }));

      cards.filter(Boolean).forEach((card) => track.appendChild(card));
      if (track.children.length) {
        section.hidden = false;
        const note = section.querySelector('[data-recently-viewed-note]');
        if (note) note.hidden = true;
      }
    });
  }

  /* ------------------------------------------- Drawer focus management (a11y) */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var lastTrigger = null;

  function openDialog() {
    return document.querySelector('[data-cart-drawer].is-open, [data-search-drawer].is-open, [data-quickadd-drawer].is-open, [data-filters-drawer].is-open')
      || document.querySelector('details[scroll-lock][open]');
  }

  function initFocusManagement() {
    // remember what opened the drawer, to restore focus on close
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-cart-open],[data-search-open],[data-filters-open],[data-quick-add],details[scroll-lock] > summary');
      if (t) lastTrigger = t;
    }, true);

    // trap Tab within the open dialog
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const d = openDialog();
      if (!d) return;
      const f = [...d.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (!d.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // focus first element on open, restore trigger on close
    document.querySelectorAll('[data-cart-drawer],[data-search-drawer],[data-quickadd-drawer],[data-filters-drawer]').forEach((d) => {
      new MutationObserver(() => {
        const open = d.classList.contains('is-open');
        if (open === d._focusOpen) return;
        d._focusOpen = open;
        if (open) {
          const f = d.querySelector(FOCUSABLE);
          if (f) setTimeout(() => f.focus(), 60);
        } else if (lastTrigger && document.body.contains(lastTrigger)) {
          lastTrigger.focus();
        }
      }).observe(d, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* --------------------------------------------- Gift card recipient */
  function initGiftRecipient(scope) {
    (scope || document).querySelectorAll('[data-gift]').forEach((box) => {
      if (box.dataset.bound) return;
      box.dataset.bound = '1';
      const toggle = box.querySelector('[data-gift-toggle]');
      if (!toggle) return;
      const fields = box.querySelectorAll('.gift-recipient__input');
      const email = box.querySelector('input[type="email"]');
      const date = box.querySelector('input[type="date"]');
      const form = box.closest('form');

      // "Send on" scheduled in the visitor's timezone
      if (form && !form.querySelector('[name="properties[__shopify_offset]"]')) {
        const off = document.createElement('input');
        off.type = 'hidden';
        off.name = 'properties[__shopify_offset]';
        off.value = String(new Date().getTimezoneOffset() * -1);
        form.appendChild(off);
      }
      if (date) {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        date.min = now.toISOString().slice(0, 10);
      }
      function sync() {
        const on = toggle.checked;
        fields.forEach((f) => { f.disabled = !on; });
        if (email) email.required = on;
      }
      toggle.addEventListener('change', sync);
      sync();
    });
  }

  function init() {
    initVariantPickers();
    initGalleries();
    initStickyBar();
    initRecommendations();
    initRecentlyViewed();
    initCollectionView();
    initGiftRecipient();
    initFocusManagement();
  }
  document.addEventListener('DOMContentLoaded', init);
  // Theme editor: re-init on section load
  document.addEventListener('shopify:section:load', (e) => {
    initVariantPickers(e.target);
    initGalleries(e.target);
    initStickyBar(e.target);
    initRecommendations(e.target);
    initRecentlyViewed(e.target);
    initGiftRecipient(e.target);
  });
})();
